import React from 'react';

export type EvidenceState = 'grounded' | 'inferred' | 'gap';

interface EvidenceChipProps {
  state: EvidenceState;
  /** Visible label, e.g. "NAICS 611519 match". */
  children: React.ReactNode;
  /** Tappable → opens source detail (popover / deep-link into profile). */
  onClick?: () => void;
  /** Extra SR context, e.g. "cited from past performance". */
  sourceHint?: string;
  className?: string;
  title?: string;
}

const MARK: Record<EvidenceState, string> = {
  grounded: '●',
  inferred: '◐',
  gap: '○',
};

const STATE_LABEL: Record<EvidenceState, string> = {
  grounded: 'Grounded',
  inferred: 'Inferred',
  gap: 'Gap — no evidence',
};

const STATE_CLASSES: Record<EvidenceState, string> = {
  grounded: 'bg-evidence-grounded-bg text-evidence-grounded border-l-2 border-solid border-evidence-grounded-border',
  inferred: 'bg-evidence-inferred-bg text-evidence-inferred border-l-2 border-solid border-evidence-inferred-border',
  gap: 'bg-evidence-gap-bg text-evidence-gap border-l-2 border-dashed border-evidence-gap-border',
};

/**
 * The Evidence Chip — BidWin's signature primitive. Every AI assertion (a score
 * factor, an analysis result, a proposal claim) carries one of these tracing
 * back to profile evidence. Three states: Grounded / Inferred / Gap. Color is
 * never the sole carrier of meaning — each chip pairs a fill with a mark glyph
 * and an SR-announced state. Tappable chips render as a real <button>.
 */
export function EvidenceChip({
  state,
  children,
  onClick,
  sourceHint,
  className = '',
  title,
}: EvidenceChipProps) {
  const base =
    'inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-r-full align-middle text-[11px] font-semibold whitespace-nowrap max-w-full';
  const interactive = onClick
    ? 'cursor-pointer hover:brightness-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-servet-blue'
    : '';
  const classes = `${base} ${STATE_CLASSES[state]} ${interactive} ${className}`;
  const srText = `${STATE_LABEL[state]}${sourceHint ? `, ${sourceHint}` : ''}`;

  const inner = (
    <>
      <span className="font-mono text-[9px] leading-none" aria-hidden="true">
        {MARK[state]}
      </span>
      <span className="sr-only">{srText}: </span>
      <span className="truncate">{children}</span>
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes} title={title}>
        {inner}
      </button>
    );
  }

  return (
    <span className={classes} title={title}>
      {inner}
    </span>
  );
}

export default EvidenceChip;
